import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { BarController, BarElement, CategoryScale, Chart, LinearScale, Legend, Tooltip } from 'chart.js';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

/**
 * Payload written by analysis-timings.ts. Kept in sync there.
 */
export interface TransactionData {
  msg: 'analysis-complete';
  analysisId?: string;
  documentId: string;
  projectId: string;
  attempt: number;
  queuedAt?: string;
  startedAt: string;
  completedAt: string;
  queueWaitMs: number;
  runMs: number;
  outcome: 'success' | 'error';
  error?: { message?: string; stage?: string; retryable?: boolean };
  apiCalls?: any;
  imageProcessing?: any;
  byStage: {
    stage: string;
    wallMs: number;
    cpuMs?: number;
    awaitMs?: number;
    eventLoopBlockedMs?: number;
    eventLoopP99Ms?: number;
    details?: any;
  }[];
  /** Joined analysis-logs.jsonl content — empty for imports (no per-stage log file). */
  logs?: Array<{ timestamp?: string; step?: string; message?: string; progress?: number; phase?: string }>;
  /**
   * Per-phase summary written by the server. `import` is present whenever an
   * import preceded this transaction; `aiAnalysis` is present when AI ran (or
   * is running — outcome === 'in-progress').
   */
  phases?: {
    import?: { outcome: string; runMs: number; startedAt?: string; completedAt?: string; errorMessage?: string };
    aiAnalysis?: { outcome: string; runMs: number; startedAt?: string; completedAt?: string; queueWaitMs?: number };
  };
  /** Server-side snapshot captured when the transaction was finalized. */
  serverContext?: {
    capturedAt: string;
    commitSha: string | null;
    commitSource: 'env' | 'git' | null;
    instanceTitle: string;
    namespace: string | null;
    nodeVersion: string;
    uptimeSeconds: number;
    busy: boolean;
    queue: { active: number; queued: number; concurrency: number };
    responsiveness: {
      eventLoopP99Ms: number;
      eventLoopMaxMs: number;
      heapUsedMb: number;
      heapTotalMb: number;
      heapUsedFraction: number;
      rssMb: number;
      gcCount: number;
      gcPauseTotalMs: number;
    };
  };
  kind?: 'import' | 'ai-analysis';
}

@Component({
  selector: 'sfo-status-detail',
  templateUrl: './status-detail.component.html',
  styleUrls: ['./status-detail.component.scss'],
  standalone: false,
})
export class StatusDetailComponent implements AfterViewInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);

  readonly transactionId = signal<string>('');
  readonly data = signal<TransactionData | null>(null);
  readonly error = signal<string | null>(null);
  readonly loading = signal<boolean>(true);

  readonly title = computed(() => {
    const id = this.transactionId();
    return id ? `Transaction ${id}` : 'Transaction';
  });

  readonly chartHeightPx = computed(() => {
    const n = this.data()?.byStage.length ?? 0;
    return Math.max(180, n * 32 + 80);
  });

  /** Total CPU vs await time across all stages — useful summary stat. */
  readonly totals = computed(() => {
    const stages = this.data()?.byStage ?? [];
    let cpu = 0;
    let awaitMs = 0;
    let blocked = 0;
    for (const s of stages) {
      cpu += s.cpuMs ?? 0;
      awaitMs += s.awaitMs ?? 0;
      blocked += s.eventLoopBlockedMs ?? 0;
    }
    return { cpu, awaitMs, blocked };
  });

  /**
   * Plain-language summary rendered at the top of the page. Three or four
   * short lines a non-technical reader can use to understand:
   *  - What this is (import vs AI analysis)
   *  - How it went (success / failure)
   *  - How long it took
   *  - Where most of the time went (slowest step), so users know whether to
   *    blame OpenAI, the network, or our own code.
   */
  readonly summary = computed(() => {
    const d = this.data();
    if (!d) return null;

    const aiPhase = d.phases?.aiAnalysis;
    const importPhase = d.phases?.import;
    const aiInProgress = aiPhase?.outcome === 'in-progress';
    const aiFinished = aiPhase && aiPhase.outcome !== 'in-progress';
    const isAi = !!aiPhase || (!!d.apiCalls && (d.apiCalls.count ?? 0) > 0);
    const success = aiInProgress ? null : d.outcome === 'success';

    let headline: string;
    if (aiInProgress) {
      headline = importPhase
        ? 'Import finished — AI analysis still running.'
        : 'AI analysis still running.';
    } else if (importPhase && aiFinished) {
      headline = success
        ? 'This document import and AI analysis finished successfully.'
        : 'This document import + AI analysis run had a failure.';
    } else {
      const kindLabel = isAi ? 'AI analysis' : 'document import';
      headline = success
        ? `This ${kindLabel} finished successfully.`
        : `This ${kindLabel} failed.`;
    }

    const durationLine = (() => {
      if (aiInProgress) {
        // Compute live elapsed from the AI startedAt so the user has a sense
        // of how long it's been running. Refreshes when the poll fires.
        const elapsed = aiPhase?.startedAt
          ? (Date.now() - new Date(aiPhase.startedAt).getTime()) / 1000
          : null;
        const importSec = importPhase ? (importPhase.runMs / 1000).toFixed(1) : null;
        return importSec
          ? `Import took ${importSec} seconds. AI analysis has been running for ${elapsed != null ? Math.round(elapsed) : '—'} seconds.`
          : `AI analysis has been running for ${elapsed != null ? Math.round(elapsed) : '—'} seconds.`;
      }
      if (importPhase && aiFinished) {
        return `Import ${(importPhase.runMs / 1000).toFixed(1)}s + AI analysis ${(aiPhase!.runMs / 1000).toFixed(1)}s · total wall time ${((importPhase.runMs + aiPhase!.runMs) / 1000).toFixed(1)} seconds.`;
      }
      const seconds = (d.runMs / 1000).toFixed(1);
      const wait = d.queueWaitMs > 0
        ? ` It waited ${(d.queueWaitMs / 1000).toFixed(1)} seconds in line before starting.`
        : '';
      return `Took ${seconds} seconds from start to finish.${wait}`;
    })();

    const slowestLine = (() => {
      if (aiInProgress) return null; // not meaningful yet
      const stages = d.byStage ?? [];
      if (stages.length === 0) return null;
      const slowest = [...stages].sort((a, b) => (b.wallMs ?? 0) - (a.wallMs ?? 0))[0];
      if (!slowest || (slowest.wallMs ?? 0) === 0) return null;
      const slowMs = slowest.wallMs ?? 0;
      const slowSeconds = (slowMs / 1000).toFixed(1);
      const fraction = d.runMs > 0 ? Math.round((slowMs / d.runMs) * 100) : 0;
      const bottleneck =
        (slowest.awaitMs ?? 0) > (slowest.cpuMs ?? 0)
          ? 'waiting on the network or storage'
          : 'running local computation';
      return `The slowest step was "${slowest.stage}" at ${slowSeconds} seconds (${fraction}% of the total), mostly ${bottleneck}.`;
    })();

    const retryLine = d.attempt > 0
      ? `This was attempt #${d.attempt + 1} — earlier tries failed and were retried automatically.`
      : null;

    const errorLine = !success && !aiInProgress && d.error
      ? `It failed at the "${d.error.stage ?? 'unknown'}" step: ${d.error.message ?? 'no details available'}.`
      : null;

    return {
      success: success === null ? true : success,
      inProgress: aiInProgress,
      headline,
      durationLine,
      slowestLine,
      retryLine,
      errorLine,
    };
  });

  @ViewChild('breakdownChart', { static: false }) breakdownCanvas?: ElementRef<HTMLCanvasElement>;
  private breakdownChart?: Chart;
  private viewReady = false;
  private pollHandle?: ReturnType<typeof setInterval>;

  /**
   * Poll cadence while the AI phase is `in-progress`. The detail page mostly
   * reads static post-completion data, but when the user lands on it mid-run
   * we re-fetch every 5s so the page refreshes with the latest logs and
   * eventually flips to the completed state on its own.
   */
  private readonly POLL_INTERVAL_MS = 5_000;

  // Watch the AI phase outcome; while it's in-progress, keep an interval
  // polling the transaction endpoint. Effect tears down the timer when the
  // outcome flips to a terminal state.
  private readonly pollWhileInProgress = effect(() => {
    const data = this.data();
    const inProgress = data?.phases?.aiAnalysis?.outcome === 'in-progress';
    if (inProgress && !this.pollHandle) {
      this.pollHandle = setInterval(() => {
        const id = this.transactionId();
        if (id) void this.load(id);
      }, this.POLL_INTERVAL_MS);
    } else if (!inProgress && this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
  });

  // Re-render whenever data changes AND the view is mounted, so the chart shows
  // up reliably no matter which one (data fetch vs view init) settles first.
  // Defers the actual draw via setTimeout so Angular's renderer has time to
  // place the canvas into the DOM after the @if/@else template branch.
  private readonly chartSync = effect(() => {
    const data = this.data();
    if (!data || !this.viewReady) return;
    setTimeout(() => this.renderChart(), 0);
  });

  constructor() {
    this.route.paramMap.subscribe((pm) => {
      const id = pm.get('transactionId') ?? '';
      this.transactionId.set(id);
      // Clear previous transaction's data immediately on navigation —
      // otherwise the chart for the old id stays rendered under the new
      // URL until (or unless) the new fetch settles. Especially visible
      // when navigating into a 404 id where the stale chart would
      // otherwise persist for the page lifetime.
      this.data.set(null);
      void this.load(id);
    });
  }

  async load(id: string) {
    if (!id) return;
    this.loading.set(true);
    this.error.set(null);
    try {
      const payload = await firstValueFrom(
        this.http.get<TransactionData>(`/api/status/transaction/${encodeURIComponent(id)}`),
      );
      // Drop late responses: while we were awaiting, the user may have
      // navigated to a different /status/<other> route. Without this guard
      // the previous fetch's payload would overwrite the new transaction's
      // data (or briefly flash before the new one settles).
      if (this.transactionId() !== id) return;
      this.data.set(payload);
      // The chartSync effect picks this up when viewReady is true.
    } catch (e: any) {
      // Same staleness check for the error path — otherwise a stale 404
      // could clobber a successful load on the new transaction.
      if (this.transactionId() !== id) return;
      // 401 here means STATUS_PASSWORD is set and the XHR had no
      // credentials. We can't drive the Basic-auth prompt from inside
      // an XHR — point the user at /status as a top-level page so the
      // browser prompts natively, after which they can navigate back.
      const msg =
        e?.status === 401
          ? 'This page is password-protected. Open /status directly in the browser to authenticate, then reload this view.'
          : e?.status === 404
          ? `No timings found for transaction "${id}". Either the id is wrong, or the analysis hasn't completed yet (sidecar is written at the end of the run).`
          : (e?.message ?? 'Failed to load transaction');
      this.error.set(msg);
      this.data.set(null);
    } finally {
      // Only release the loading flag for the currently-displayed id; a
      // stale load completing later shouldn't flip loading=false on the
      // new id's still-pending request.
      if (this.transactionId() === id) {
        this.loading.set(false);
      }
    }
  }

  ngAfterViewInit() {
    this.viewReady = true;
    if (this.data()) {
      setTimeout(() => this.renderChart(), 0);
    }
  }

  ngOnDestroy() {
    this.breakdownChart?.destroy();
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
  }

  /**
   * Bundle the full transaction payload (timings, byStage, phases, apiCalls,
   * image processing, logs, server context) into a JSON file and trigger a
   * browser download. Useful for sharing a failing run with someone for
   * diagnosis without having to copy URLs or screenshot the page.
   */
  downloadTransaction() {
    const payload = this.data();
    if (!payload) return;
    const enriched = {
      // Small metadata header so the recipient knows what they're looking at.
      // The rest is the raw transaction record straight from the server,
      // including the server-context snapshot captured at write time.
      _downloadedAt: new Date().toISOString(),
      _viewedAt: location.href,
      ...payload,
    };
    const blob = new Blob([JSON.stringify(enriched, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transaction-${this.transactionId() || 'unknown'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }

  private renderChart() {
    const canvas = this.breakdownCanvas?.nativeElement;
    const stages = this.data()?.byStage ?? [];
    if (!canvas || stages.length === 0) return;

    const labels = stages.map((s) => s.stage);
    // CPU = how long the stage was actively burning CPU; await = how long it was
    // waiting on I/O / network (OpenAI, S3). Stacked horizontally so the bar's
    // total length = wallMs and the split shows which kind of work dominated.
    const cpuData = stages.map((s) => s.cpuMs ?? 0);
    const awaitData = stages.map((s) => s.awaitMs ?? 0);

    if (!this.breakdownChart) {
      this.breakdownChart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'CPU (active work)',
              data: cpuData,
              backgroundColor: '#f97316',
              borderRadius: 4,
              stack: 'time',
            },
            {
              label: 'Await (I/O, network)',
              data: awaitData,
              backgroundColor: '#3b82f6',
              borderRadius: 4,
              stack: 'time',
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x} ms`,
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              title: { display: true, text: 'milliseconds', font: { size: 11 } },
              grid: { color: '#f0f1f3' },
            },
            y: {
              stacked: true,
              grid: { display: false },
            },
          },
        },
      });
    } else {
      this.breakdownChart.data.labels = labels;
      this.breakdownChart.data.datasets[0].data = cpuData;
      this.breakdownChart.data.datasets[1].data = awaitData;
      this.breakdownChart.update('none');
    }
  }
}
